import * as React from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import PercentageNumber from "../../../components/UI/PercentageNumber";
import RRoR from "../../../components/UI/RRoR";
import moment from "moment";
import {
  getRiskPeriod,
  getRiskPeriodLossTolerance,
} from "../../../utilities/cpr";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import HighLevelDisclosure from "./HighLevelDisclosure";
import { isBasicView } from "../../../utilities/access";

const SummarySection = (props) => {
  const { portfolio, baseChartData, benchmarkName, accessLevel } = props;
  const {
    name,
    cprRecommendation,
    Clients,
    metadata,
    Household,
  } = portfolio;
  const useWbiSingleStrategy = ['WBI Portfolios', 'WBI Envestnet', 'Other Models'].includes(metadata.managerType);
  const strategyStartDate = moment(baseChartData[0].date)
    .startOf("month")
    .subtract(2, "months");

  return (
    <section
      className={"content"}
      data-toc={`Cy Optimized Portfolio Summary`}
    >
      <Grid container={true} spacing={3}>
        <Grid item={true} xs={12}>
          <Typography variant="h4" gutterBottom={true}>
            Cy Optimized Portfolio Summary
          </Typography>
          <Typography variant="h5">{Household.name}</Typography>
          <Typography variant="h6" gutterBottom={true}>
            {name} - Hypothetical Illustration
          </Typography>
        </Grid>
        <Grid item={true} xs={4}>
          <Typography variant="body1">
            <b>
              {Clients[0].profile.firstName}
              {Clients.length > 1 && (
                <React.Fragment>
                  {" "}
                  and <span>{Clients[1].profile.firstName}</span>
                </React.Fragment>
              )}
              ,{" "}
            </b>
            now let’s review the portfolio that{" "}
            {useWbiSingleStrategy
              ? "you selected"
              : `Cy optimized for you`}{" "}
            today. We will also review the information you provided{" "}
            {useWbiSingleStrategy ? "" : "to Cy"} which was used to
            optimize the portfolio and provide you some additional information
            regarding the strategy selections as well. If everything looks good,
            we can begin the onboarding process.
          </Typography>
        </Grid>
        <Grid item={true} xs={4}>
          <Typography variant="body1">
            We believe the most important thing you can do as an investor is
            determine your Loss Tolerance. Based on what you entered, your Loss
            Tolerance for {getRiskPeriod(metadata.riskPeriod)} is&nbsp;
            <b>
              <PercentageNumber value={metadata.risk} showSign={true} />
            </b>
            .&nbsp;
            {useWbiSingleStrategy ? "We" : "Cy"} also estimated that you
            need a&nbsp;
            <b>
              <RRoR rrorValue={metadata.rror} />
            </b>
            &nbsp; annualized rate of return on investments over time to meet
            your retirement goals (your Required Rate of Return).
          </Typography>
        </Grid>
        <Grid item={true} xs={4}>
          <Typography variant="body1">
            Most investors compare their portfolio results to the market. A
            popular benchmark for the market is the {benchmarkName}. Let’s see
            how the hypothetical performance and risk characteristics compare
            between your proposed portfolio and&nbsp; the {benchmarkName} since{" "}
            {strategyStartDate.toDate().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            .
          </Typography>
        </Grid>
      </Grid>

      <Grid container={true} spacing={3} style={{ marginTop: "1in" }}>
        <Grid item={true} xs={12}>
          <Typography variant="h5">
            {useWbiSingleStrategy
              ? "Selected Portfolio"
              : "Cy Optimized Portfolio"}{" "}
            vs. {benchmarkName}
          </Typography>
        </Grid>
        <Grid item={true} xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="center">Your Needs</TableCell>
                <TableCell align="center">Your Selection</TableCell>
                <TableCell align="center">
                  {useWbiSingleStrategy
                    ? "Selected Portfolio"
                    : "Cy Optimized Portfolio"}{" "}
                  <sup>*</sup>
                </TableCell>
                <TableCell align="center">
                  {benchmarkName} <sup>**</sup>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {getRiskPeriod(metadata.riskPeriod)} (Loss Tolerance)
                </TableCell>
                <TableCell align="center">
                  <PercentageNumber value={metadata.risk} showSign={true} />
                </TableCell>
                <TableCell align="center">
                  {useWbiSingleStrategy ? (
                    "--"
                  ) : metadata.solveFor === "risk" ? (
                    <PercentageNumber value={parseFloat(metadata.cprRisk)} />
                  ) : (
                    "--"
                  )}
                </TableCell>

                <TableCell align="center">
                {!isBasicView(accessLevel) ? (
                  <PercentageNumber
                    value={
                      cprRecommendation.summary.recommendationSummary[
                        getRiskPeriodLossTolerance(metadata.riskPeriod)
                      ]
                    }
                    showSign={true}
                  />) : '--'
                }
                </TableCell>
                <TableCell align="center">
                  <PercentageNumber
                    value={
                      cprRecommendation.summary.benchmark[
                        getRiskPeriodLossTolerance(metadata.riskPeriod)
                      ]
                    }
                    showSign={true}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Annualized Rate of Return (RRoR)</TableCell>
                <TableCell align="center">
                  <RRoR rrorValue={metadata.rror} />
                </TableCell>
                <TableCell align="center">
                  {useWbiSingleStrategy ? (
                    "--"
                  ) : metadata.solveFor === "ret" ? (
                    <PercentageNumber value={parseFloat(metadata.cprRror)} />
                  ) : (
                    "--"
                  )}
                </TableCell>
                <TableCell align="center">
                  {!isBasicView(accessLevel) ? (<PercentageNumber
                    value={
                      cprRecommendation.summary.recommendationSummary
                        .AnnualizedReturn
                    }
                    showSign={true}
                  />) : '--'
                  }
                </TableCell>
                <TableCell align="center">
                  <PercentageNumber
                    value={cprRecommendation.summary.benchmark.AnnualizedReturn}
                    showSign={true}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>
      <HighLevelDisclosure />
    </section>
  );
};

export default SummarySection;



// WEBPACK FOOTER //
// ./src/containers/SummaryPDF/components/SummarySection.jsx